"use client";

import defaultAvatar from "@/assets/global/default-avatar.webp";
import EmptyState from "@/components/portfolio/empty-state";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { SmartImage } from "@/components/ui/smart-image";
import { cn } from "@/lib/utils";
import { useUserInfo } from "@/store/user-info";
import { get, getOptionLabel, type MatchupOptionLabelInput } from "@/utils/utils";
import { useRouter } from "next/navigation";
import FilterSelect from "../components/filter-select";
import { useHoldersTab } from "../holders/useHoldersTab";

interface GuessOption {
    guessNo: string;
    title: string;
}

interface HoldersTabProps {
    guessList: GuessOption[];
    matchups?: MatchupOptionLabelInput[];
}

export default function HoldersTab( { guessList }: HoldersTabProps ) {
    const router = useRouter();
    const currentUserId = useUserInfo( ( state ) => state.userId );
    const {
        loading,
        yesHolders,
        noHolders,
        guessOptions,
        selectedGuessNo,
        handleGuessChange,
        showGuessFilter,
        activeGuess,
    } = useHoldersTab( guessList );

    // 获取 Yes 和 No 选项的标签
    const yesLabel = getOptionLabel( "yes", activeGuess );
    const noLabel = getOptionLabel( "no", activeGuess );

    const renderEmpty = () => <EmptyState text="No holders data" className="h-auto py-24" textClassName="text-sm" />;

    const renderHolderItem = ( holder: (typeof yesHolders)[number], type: "yes" | "no" = "yes" ) => {
        const navigateToProfile = ( targetUserId: string ) => {
            if ( !targetUserId ) return;
            if ( String( targetUserId ) === String( currentUserId ?? "" ) ) {
                router.push( "/profile" );
                return;
            }
            router.push( `/profile?userId=${targetUserId}` );
        };

        const getRankBadgeColor = ( rank: number ) => {
            if ( rank === 1 ) return "#E35C0B";
            if ( rank === 2 ) return "#96AAB4";
            if ( rank === 3 ) return "#BD7F6E";
            return "#000";
        };

        const contractsColorClass = type === "no" ? "text-no" : "text-yes";
        const avatarSrc = holder.avatar || defaultAvatar.src;

        return (
            <div key={holder.id} className="flex items-center gap-3 border-b border-stroke pb-[9px] pt-2">
                <div className="relative flex shrink-0 flex-row items-center">
                    <button
                        type="button"
                        onClick={( event ) => {
                            event.stopPropagation();
                            navigateToProfile( get( holder, "userId", "" ) );
                        }}
                        className="size-8 overflow-hidden rounded-full bg-surface-muted"
                    >
                        <SmartImage
                            src={avatarSrc}
                            alt={`${holder.username} avatar`}
                            width={32}
                            height={32}
                            className="size-full object-cover"
                            onError={( event ) => {
                                if ( event.currentTarget.src !== defaultAvatar.src ) {
                                    event.currentTarget.src = defaultAvatar.src;
                                }
                            }}
                            imageUrlOptions={{ width: 150, height: 150 }}
                        />
                    </button>
                    <div
                        className="absolute -right-1.5 -top-[3px] flex size-5 items-center justify-center rounded-full"
                        style={{ backgroundColor: getRankBadgeColor( holder.rank ) }}
                    >
                        <p className="text-[10px] font-semibold leading-[15px] text-content-fixed-inverse">
                            {holder.rank}
                        </p>
                    </div>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <button
                        type="button"
                        className="max-w-full truncate text-left text-[14px] font-medium leading-normal text-t1 hover:underline focus-visible:outline-none"
                        title={holder.username || "-"}
                        onClick={( event ) => {
                            event.stopPropagation();
                            navigateToProfile( get( holder, "userId", "" ) );
                        }}
                    >
                        {holder.username || "-"}
                    </button>
                    <p className={cn( "text-xs font-medium leading-normal", contractsColorClass )}>
                        {holder.contracts.toLocaleString()} shares
                    </p>
                </div>
            </div>
        );
    };

    // Convert guessOptions to FilterSelect format
    const filterOptions = guessOptions.map( ( guess ) => ( { value: guess.guessNo, label: guess.title } ) );

    // 1. 加载状态：统一显示一个 loading
    if ( loading ) {
        return (
            <div className="flex flex-col gap-3">
                {showGuessFilter && filterOptions.length > 1 && (
                    <div className="flex w-full items-center justify-start">
                        <FilterSelect
                            value={selectedGuessNo}
                            onValueChange={handleGuessChange}
                            options={filterOptions}
                            placeholder="Select guess"
                            className="w-full"
                        />
                    </div>
                )}
                <div className="flex items-center justify-center py-24">
                    <LoadingSpinner size="lg" />
                </div>
            </div>
        );
    }

    // 2. 判断是否两个列表都为空
    const bothEmpty = yesHolders.length === 0 && noHolders.length === 0;

    // 3. 如果两个列表都为空，显示统一的空状态
    if ( bothEmpty ) {
        return (
            <div className="flex flex-col gap-3">
                {showGuessFilter && filterOptions.length > 1 && (
                    <div className="flex w-full items-center justify-start">
                        <FilterSelect
                            value={selectedGuessNo}
                            onValueChange={handleGuessChange}
                            options={filterOptions}
                            placeholder="Select guess"
                            className="w-full"
                        />
                    </div>
                )}
                <EmptyState text="No holders data" className="h-auto py-24" textClassName="text-sm" />
            </div>
        );
    }

    // 4. 正常渲染两个列表，各自判断是否为空
    return (
        <div className="flex flex-col gap-2">
            {showGuessFilter && filterOptions.length > 1 && (
                <div className="flex w-full items-center justify-start">
                    <FilterSelect
                        value={selectedGuessNo}
                        onValueChange={handleGuessChange}
                        options={filterOptions}
                        placeholder="Select guess"
                        className="w-full"
                    />
                </div>
            )}

            <div className="flex gap-3">
                {/* Yes holders column */}
                <div className="min-w-0 flex-1 overflow-x-hidden">
                    <div className="flex h-11 items-center border-b border-stroke pb-px">
                        <h3 className="flex min-w-0 items-center text-[14px] font-semibold leading-normal text-t1">
                            <span className="max-w-[calc(100%-50px)] truncate">{yesLabel}</span>
                            <span className="ml-1 shrink-0">holders</span>
                        </h3>
                    </div>
                    {yesHolders.length === 0
                        ? ( renderEmpty() )
                        : <div>{yesHolders.map( ( holder ) => renderHolderItem( holder, "yes" ) )}</div>}
                </div>

                {/* No holders column */}
                <div className="min-w-0 flex-1 overflow-x-hidden">
                    <div className="flex h-11 items-center border-b border-stroke pb-px">
                        <h3 className="flex min-w-0 items-center text-[14px] font-semibold leading-normal text-t1">
                            <span className="max-w-[calc(100%-50px)] truncate">{noLabel}</span>
                            <span className="ml-1 shrink-0">holders</span>
                        </h3>
                    </div>
                    {noHolders.length === 0
                        ? ( renderEmpty() )
                        : <div>{noHolders.map( ( holder ) => renderHolderItem( holder, "no" ) )}</div>}
                </div>
            </div>
        </div>
    );
}
